"use no memo";

// TanStack Table v8 mutates stable column instances; filter state must be read live.

import { CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { Column } from "@tanstack/react-table";
import * as React from "react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { Separator } from "@/src/components/shadcn/ui/separator";
import { cn } from "@/src/lib/shadcn-utils";

export interface FacetedFilterOption {
  label: string;
  value: string;
  description?: string;
  icon?: React.ElementType;
  iconWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  /** Icon color/style override (e.g. the option's status color); defaults to muted. */
  iconClassName?: string;
}

export function nextFacetedSelection(
  selectedValues: Set<string>,
  optionValue: string,
  maxSelections?: number
): string[] | null {
  const isSelected = selectedValues.has(optionValue);

  if (maxSelections === 1) {
    return isSelected ? null : [optionValue];
  }

  const next = new Set(selectedValues);

  if (isSelected) {
    next.delete(optionValue);
  } else {
    next.add(optionValue);
  }

  const filterValues = Array.from(next);

  return filterValues.length ? filterValues : null;
}

interface FacetedFilterCommandProps {
  title?: string;
  options: FacetedFilterOption[];
  selectedValues: Set<string>;
  onChange: (values: string[] | null) => void;
  facets?: Map<string, number>;
  maxSelections?: number;
}

/**
 * The shared Command body (search + checkbox list + clear) used by both the
 * toolbar faceted filter and the in-header column filter. Kept separate so the
 * two triggers can render identical option lists.
 */
export function FacetedFilterCommand({
  title,
  options,
  selectedValues,
  onChange,
  facets,
  maxSelections,
}: FacetedFilterCommandProps) {
  return (
    <Command>
      <CommandInput placeholder={title} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {selectedValues.size > 0 && (
          <>
            <CommandGroup>
              <CommandItem onSelect={() => onChange(null)} className="justify-center text-center">
                Clear filters
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup>
          {options.map((option) => {
            const isSelected = selectedValues.has(option.value);

            return (
              <CommandItem
                key={option.value}
                value={[option.label, option.description, option.value].filter(Boolean).join(" ")}
                onSelect={() => onChange(nextFacetedSelection(selectedValues, option.value, maxSelections))}
              >
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-foreground",
                    isSelected ? "bg-foreground text-background" : "opacity-50 [&_svg]:invisible"
                  )}
                >
                  <CheckIcon className={cn("h-4 w-4")} />
                </div>
                {option.icon ? (
                  <option.icon
                    className={cn("mr-2 h-4 w-4", option.iconClassName ?? "text-muted-foreground")}
                    {...(option.iconWeight ? { weight: option.iconWeight } : {})}
                  />
                ) : null}
                <span className="min-w-0">
                  <span>{option.label}</span>
                  {option.description && (
                    <span className="ml-1 text-xs text-muted-foreground">{option.description}</span>
                  )}
                </span>
                {facets?.get(option.value) && (
                  <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                    {facets.get(option.value)}
                  </span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: FacetedFilterOption[];
  onChange: (values: string[] | null) => void;
  externalFacets?: Map<string, number>;
  disabled?: boolean;
  maxSelections?: number;
  /**
   * Controlled selection for column-less use (e.g. the comms inbox sidebar,
   * which filters a plain list rather than a TanStack table). When omitted, the
   * selection is read from `column.getFilterValue()`.
   */
  selectedValues?: string[];
  /** Fires when the popover opens/closes, e.g. to lazily fetch this filter's counts on first open. */
  onOpenChange?: (open: boolean) => void;
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  onChange,
  externalFacets,
  disabled = false,
  maxSelections,
  selectedValues: controlledValues,
  onOpenChange,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const facets = externalFacets || column?.getFacetedUniqueValues();
  const selectedValues = new Set(controlledValues ?? (column?.getFilterValue() as string[]));

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-accent border-dashed" disabled={disabled}>
          <PlusCircledIcon className="mr-2 h-4 w-4" />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        title={option.label}
                        className="block max-w-16 truncate rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <FacetedFilterCommand
          title={title}
          options={options}
          selectedValues={selectedValues}
          onChange={onChange}
          facets={facets}
          maxSelections={maxSelections}
        />
      </PopoverContent>
    </Popover>
  );
}
